import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import MedicationIcon from "@mui/icons-material/Medication";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import {
  IPD_COLORS,
  IPD_SHAPE,
  ipdElevatedInputSx,
  IpdButton,
  IpdTextField,
} from "../../ipd/utils";
import { expandOrderSet, listOrderSets, placeOrders } from "../api/emergencyApi";

/**
 * Apply a predefined order set (ticket 17).
 *
 * Order sets are offered by the pharmacy CPOE service and are medicine-only. The
 * provider picks one, its contents appear as editable medicine orders, they trim
 * or mark items urgent, and submitting funnels through the SAME place-orders path
 * a manual entry uses — so the orders created, and where they go, are identical.
 *
 * The set/item field names below are read defensively across the likely shapes:
 * the contract is owned by the external service and is confirmed there, not here.
 * Until it is configured, this dialog exercises its loading and error states.
 */

const setId = (s) => s.id ?? s.order_set_id ?? s.uuid ?? s.code;
const setName = (s) => s.name || s.title || s.order_set_name || s.label || `Set ${setId(s)}`;
const setDesc = (s) => s.description || s.summary || s.specialty || "";

const itemName = (it) =>
  it.name || it.medicine_name || it.drug_name || it.item_name || it.display_name || it.medicine || "";
const itemInstr = (it) =>
  it.instructions || it.sig || it.dosage || it.dose || it.frequency || "";

/** Pull the item array out of whatever envelope /expand returns. */
function extractItems(expanded) {
  if (Array.isArray(expanded)) return expanded;
  if (!expanded || typeof expanded !== "object") return [];
  return expanded.items || expanded.medicines || expanded.orders || expanded.lines ||
    (expanded.order_set && expanded.order_set.items) || [];
}

export default function OrderSetDialog({ open, visit, onClose, onApplied }) {
  const [phase, setPhase] = useState("list"); // "list" | "review"

  const [sets, setSets] = useState(null);
  const [loadingSets, setLoadingSets] = useState(false);
  const [setsError, setSetsError] = useState("");

  const [chosen, setChosen] = useState(null);
  const [lines, setLines] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [itemsError, setItemsError] = useState("");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadSets = useCallback(async () => {
    setLoadingSets(true);
    setSetsError("");
    try {
      setSets(await listOrderSets());
    } catch (e) {
      setSetsError(e?.message || "Could not load order sets");
    } finally {
      setLoadingSets(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setPhase("list");
    setChosen(null);
    setLines([]);
    setError("");
    setItemsError("");
    loadSets();
  }, [open, loadSets]);

  const choose = async (set) => {
    setChosen(set);
    setLoadingItems(true);
    setItemsError("");
    setPhase("review");
    try {
      const expanded = await expandOrderSet(setId(set));
      const mapped = extractItems(expanded).map((it) => ({
        item_name: itemName(it),
        instructions: itemInstr(it),
        include: true,
        urgent: false,
      }));
      setLines(mapped);
    } catch (e) {
      setItemsError(e?.message || "Could not load this set's contents");
      setLines([]);
    } finally {
      setLoadingItems(false);
    }
  };

  const setLine = (index, patch) =>
    setLines(lines.map((l, i) => (i === index ? { ...l, ...patch } : l)));

  const included = lines.filter((l) => l.include && l.item_name.trim());

  const apply = async () => {
    if (included.length === 0) return;
    setSaving(true);
    setError("");
    try {
      const payload = included.map((l) => ({
        order_type: "medicine",
        item_name: l.item_name.trim(),
        urgent: l.urgent,
        instructions: l.instructions.trim() || undefined,
      }));
      const result = await placeOrders(visit.id, payload);
      await onApplied?.(result.message);
      onClose();
    } catch (e) {
      setError(e?.message || "Could not place the orders");
    } finally {
      setSaving(false);
    }
  };

  if (!visit) return null;

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: `${IPD_SHAPE.radiusLg}px` } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography sx={{ fontSize: 17, fontWeight: 700, color: IPD_COLORS.textPrimary }}>
          {phase === "list" ? "Apply an order set" : setName(chosen || {})}
        </Typography>
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, mt: 0.25 }}>
          {phase === "list"
            ? "A ready-made set of medicines for a common presentation — trim it for this patient before placing."
            : "Uncheck what does not apply, mark anything urgent, then place. These become ordinary medicine orders."}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* -------------------------------------------------- pick a set */}
        {phase === "list" && (
          <>
            {setsError && (
              <Alert
                severity="error"
                sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}
                action={
                  <IpdButton variant="outlined" size="small" onClick={loadSets}>
                    Retry
                  </IpdButton>
                }
              >
                {setsError}
              </Alert>
            )}
            {loadingSets ? (
              <Box sx={{ display: "grid", gap: 1 }}>
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} variant="rounded" height={54} />
                ))}
              </Box>
            ) : !setsError && (!sets || sets.length === 0) ? (
              <Box
                sx={{
                  textAlign: "center",
                  py: 3,
                  px: 2,
                  border: `1px dashed ${IPD_COLORS.borderHover}`,
                  borderRadius: `${IPD_SHAPE.radiusMd}px`,
                }}
              >
                <MedicationIcon sx={{ fontSize: 26, color: IPD_COLORS.textMuted }} />
                <Typography sx={{ mt: 1, fontSize: 14, fontWeight: 700 }}>
                  No order sets available
                </Typography>
                <Typography sx={{ mt: 0.5, fontSize: 13, color: IPD_COLORS.textSecondary }}>
                  The pharmacy service offers none for this facility, or is not configured.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {(sets || []).map((s) => (
                  <Box
                    key={setId(s)}
                    role="button"
                    tabIndex={0}
                    onClick={() => choose(s)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        choose(s);
                      }
                    }}
                    sx={{
                      px: 1.75,
                      py: 1.25,
                      cursor: "pointer",
                      border: `1px solid ${IPD_COLORS.border}`,
                      borderRadius: `${IPD_SHAPE.radiusMd}px`,
                      "&:hover": { borderColor: IPD_COLORS.borderHover, bgcolor: IPD_COLORS.pageBg },
                      "&:focus-visible": { outline: `2px solid ${IPD_COLORS.primary}`, outlineOffset: 2 },
                    }}
                  >
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{setName(s)}</Typography>
                    {setDesc(s) && (
                      <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary }}>
                        {setDesc(s)}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}

        {/* -------------------------------------------------- review items */}
        {phase === "review" && (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }} onClose={() => setError("")}>
                {error}
              </Alert>
            )}
            {itemsError && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}>
                {itemsError}
              </Alert>
            )}
            {loadingItems ? (
              <Box sx={{ display: "grid", gap: 1 }}>
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} variant="rounded" height={48} />
                ))}
              </Box>
            ) : lines.length === 0 && !itemsError ? (
              <Typography sx={{ fontSize: 13.5, color: IPD_COLORS.textSecondary }}>
                This set has no items.
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {lines.map((line, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "auto 1fr auto",
                      alignItems: "center",
                      gap: 1,
                      px: 1,
                      py: 0.75,
                      borderRadius: `${IPD_SHAPE.radiusMd}px`,
                      border: `1px solid ${IPD_COLORS.border}`,
                      opacity: line.include ? 1 : 0.5,
                    }}
                  >
                    <Checkbox
                      checked={line.include}
                      onChange={(e) => setLine(index, { include: e.target.checked })}
                      size="small"
                      inputProps={{ "aria-label": `Include ${line.item_name}` }}
                      sx={{ color: IPD_COLORS.textMuted, "&.Mui-checked": { color: IPD_COLORS.primary } }}
                    />
                    <Box sx={{ minWidth: 0, display: "grid", gap: 0.5 }}>
                      <IpdTextField
                        value={line.item_name}
                        onChange={(e) => setLine(index, { item_name: e.target.value })}
                        size="small"
                        fullWidth
                        disabled={!line.include}
                        sx={ipdElevatedInputSx}
                        inputProps={{ "aria-label": "Medicine" }}
                      />
                      {(line.instructions || line.include) && (
                        <IpdTextField
                          value={line.instructions}
                          onChange={(e) => setLine(index, { instructions: e.target.value })}
                          placeholder="Dose, route, frequency"
                          size="small"
                          fullWidth
                          disabled={!line.include}
                          sx={ipdElevatedInputSx}
                          inputProps={{ "aria-label": "Instructions" }}
                        />
                      )}
                    </Box>
                    <Tooltip title="Mark urgent">
                      <Box
                        component="button"
                        type="button"
                        aria-pressed={line.urgent}
                        aria-label="Mark urgent"
                        disabled={!line.include}
                        onClick={() => setLine(index, { urgent: !line.urgent })}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.25,
                          px: 0.9,
                          py: 0.6,
                          cursor: line.include ? "pointer" : "default",
                          fontFamily: "inherit",
                          fontSize: 11.5,
                          fontWeight: 700,
                          borderRadius: `${IPD_SHAPE.radiusSm}px`,
                          border: `1px solid ${line.urgent ? IPD_COLORS.error.text : IPD_COLORS.border}`,
                          color: line.urgent ? IPD_COLORS.error.text : IPD_COLORS.textMuted,
                          bgcolor: line.urgent ? IPD_COLORS.error.bg : "transparent",
                        }}
                      >
                        <BoltIcon sx={{ fontSize: 13 }} />
                        STAT
                      </Box>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: "space-between" }}>
        {phase === "review" ? (
          <IpdButton
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={() => setPhase("list")}
            disabled={saving}
          >
            Back
          </IpdButton>
        ) : (
          <span />
        )}
        <Box sx={{ display: "flex", gap: 1 }}>
          <IpdButton variant="outlined" onClick={onClose} disabled={saving}>
            Cancel
          </IpdButton>
          {phase === "review" && (
            <IpdButton onClick={apply} loading={saving} disabled={included.length === 0}>
              {included.length ? `Place ${included.length} order${included.length > 1 ? "s" : ""}` : "Nothing selected"}
            </IpdButton>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
